import type { SettingSubPositionOption} from '../Types';
import type { LengthUnit} from '@easterngraphics/wcf/modules/utils/number';
import type App from '@egr/xbox/base-app/App';

import i18next from 'i18next';

import { getAllowedSettingsValueTypeString } from '../Types';

import { RotationSetting, SnapshotFormat } from './Types';

import { LanguageMap } from '@egr/xbox/utils/Language';
import { timeout } from '@egr/xbox/utils/Promise';
import { getEnvNumber } from '@egr/xbox/utils/ReactScriptHelper';

import { toLocaleLengthString } from '@easterngraphics/wcf/modules/utils/number';
import { isNotNullOrEmpty, isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

export const ANIMATION_DURATION: number = getEnvNumber('ANIMATION_DURATION', 0);

/**
 * Always returns an string even if primary and fallback are undefined/null (empty string in this case)
 * @param primary
 * @param fallback
 */
export function getStringValue(primary: string | undefined, fallback?: string): string {
    if (primary == null) {
        return fallback ?? '';
    }
    return primary;
}

export function snapshotFormatToString(value: SnapshotFormat | undefined): string {
    if (value == null) {
        return i18next.t('Unknown');
    }
    return value === SnapshotFormat.TRANSPARENT ? i18next.t('Transparent background') : i18next.t('White background');
}

export function layoutToLabel(value?: boolean): string {
    if (value) {
        return i18next.t('Detailed view');
    }
    return i18next.t('Compact view');
}

export function catalogViewModeToLabel(value?: string): string {
    if (value === 'grid') {
        return i18next.t('Grid view');
    }
    return i18next.t('List view');
}

export function sliderOptionToLabel(value?: boolean): string {
    if (value) {
        return i18next.t('Comparison view');
    }
    return i18next.t('List view');
}

export function settingToLabel(value?: boolean, content?: string): string {
    if (!value) {
        return i18next.t('Hidden');
    }
    if (isNotNullOrEmpty(content)) {
        return content;
    }
    return i18next.t('Shown');
}

export function validString(value?: string): string {
    if (value == null) {
        return i18next.t('Unknown');
    }
    return value;
}

export function languageToLabelNotTranslated(value?: string): string {
    if (isNullOrEmpty(value)) {
        return i18next.t('Unknown');
    }
    try {
        const options: Intl.DisplayNamesOptions = { type: 'language', languageDisplay: 'standard', fallback: 'none' };
        const nativeValue = (new Intl.DisplayNames(value, options)).of(value);
        if (isNotNullOrEmpty(nativeValue)) {
            return nativeValue.replace(/^./, nativeValue[0].toLocaleUpperCase());
        }
    } catch {
        /* */
    }

    return LanguageMap.get(value) ?? value;
}

// eslint-disable-next-line complexity
export function getFallbackLanguageLabel(locale: string, lng?: string): string {
    switch (locale) {
        case 'bg': return i18next.t('Bulgarian', { lng: lng ?? i18next.language});
        case 'cs': return i18next.t('Czech', { lng: lng ?? i18next.language}); // Pootle uses cs for czech so we have to use cs for userLanguage setting
        case 'cz': return i18next.t('Czech', { lng: lng ?? i18next.language});
        case 'da': return i18next.t('Danish', { lng: lng ?? i18next.language});
        case 'de': return i18next.t('German', { lng: lng ?? i18next.language});
        case 'en': return i18next.t('English', { lng: lng ?? i18next.language});
        case 'es': return i18next.t('Spanish', { lng: lng ?? i18next.language});
        case 'fi': return i18next.t('Finnish', { lng: lng ?? i18next.language});
        case 'fr': return i18next.t('French', { lng: lng ?? i18next.language});
        case 'ga': return i18next.t('Irish', { lng: lng ?? i18next.language});
        case 'hu': return i18next.t('Hungarian', { lng: lng ?? i18next.language});
        case 'it': return i18next.t('Italian', { lng: lng ?? i18next.language});
        case 'lv': return i18next.t('Latvian', { lng: lng ?? i18next.language});
        case 'nl': return i18next.t('Dutch', { lng: lng ?? i18next.language});
        case 'no': return i18next.t('Norwegian', { lng: lng ?? i18next.language});
        case 'pl': return i18next.t('Polish', { lng: lng ?? i18next.language});
        case 'pt': return i18next.t('Portuguese', { lng: lng ?? i18next.language});
        case 'ro': return i18next.t('Romanian', { lng: lng ?? i18next.language});
        case 'ru': return i18next.t('Russian', { lng: lng ?? i18next.language});
        case 'sk': return i18next.t('Slovak', { lng: lng ?? i18next.language});
        case 'sv': return i18next.t('Swedish', { lng: lng ?? i18next.language});
        case 'tr': return i18next.t('Turkish', { lng: lng ?? i18next.language});
        default: return i18next.t('Unknown', { lng: lng ?? i18next.language});
    }
}

const languageDisplayOption: Intl.DisplayNamesOptions = { type: 'language', languageDisplay: 'standard', fallback: 'none' };

export function getNativeLanguage(locale: string): string {
    return (new Intl.DisplayNames(locale, languageDisplayOption)).of(locale) ?? getFallbackLanguageLabel(locale, locale);
}

export function getTranslatedLanguage(locale: string): string {
    return (new Intl.DisplayNames(i18next.language, languageDisplayOption)).of(locale) ?? getFallbackLanguageLabel(locale);
}

export function languageToLabel(value: string | undefined): string {
    if (isNotNullOrEmpty(value)) {
        try {
            return [getTranslatedLanguage(value), getNativeLanguage(value)].join(' - ');
        } catch {
            return getFallbackLanguageLabel(value);
        }
    }
    return i18next.t('Unknown');
}

export function booleanToLabel(value: boolean | undefined): string {
    if (value) {
        return i18next.t('Yes');
    }
    return i18next.t('No');
}

export interface SnappingSetup {
    active: boolean;
    /**
     * saved in meter
     */
    value: number;
    customValue: number;
    custom: boolean;
}

export function snappingToLabel(snapping: SnappingSetup, app: App, lengthUnit: LengthUnit): string {
    if (!snapping.active) {
        return i18next.t('Disabled');
    }

    const lengthValue: number = snapping.custom ? snapping.customValue : snapping.value;

    const value: string | null = lengthValue === 0 ?
        null :
        toLocaleLengthString(
            lengthValue,
            app.coreApp.i18n.locale,
            lengthUnit
        );
    return `${i18next.t('Enabled')} - ${value ?? i18next.t('Dynamic')}`;
}

export function snappingValueToLabel(value: number | undefined, app: App, lengthUnit: LengthUnit): string {
    if (value === 0 || value == null) {
        return i18next.t('Dynamic');
    }

    const lengthString: string | null = toLocaleLengthString(
        value,
        app.coreApp.i18n.locale,
        lengthUnit
    );

    return i18next.t('{{value}} Raster', {value: lengthString, interpolation: { escapeValue: false }});
}

export function rotationToLabel(value: RotationSetting | undefined): string {
    switch (value) {
        case RotationSetting.ONE: return i18next.t('One');
        case RotationSetting.THREE: return i18next.t('Three');
        default: return i18next.t('Unknown');
    }
}

export function getAsyncOption<ValueType, AppType extends App = App>(
    changeCallback: (value: ValueType, app?: AppType) => Promise<void>,
    updateViewCallback: (app?: AppType) => void,
    closeCallback: VoidFunction | undefined,
    toString: (value: ValueType | undefined, app?: AppType) => string,
    toIcon?: (value: ValueType | undefined) => React.ReactNode,
    toLabel?: (value: ValueType | undefined, app?: AppType) => React.ReactNode,
): (value: ValueType) => SettingSubPositionOption<ValueType> {
    return (value: ValueType, app?: AppType): SettingSubPositionOption<ValueType> => {
        return {
            label: toLabel?.(value, app) ?? toString(value, app),
            icon: toIcon,
            value: value,
            'data-testid': getAllowedSettingsValueTypeString<ValueType>(value, toString(value, app)),
            set: async (): Promise<void> => {
                if (closeCallback != null && ANIMATION_DURATION !== 0) {
                    await timeout(ANIMATION_DURATION);
                }
                // apply change
                await changeCallback(value);
                // show change
                updateViewCallback(app);
                // close
                if (closeCallback != null) {
                    window.setTimeout(closeCallback, 200);
                }
            }
        };
    };
}

export function getOption<ValueType, AppType extends App = App>(
    changeCallback: (value: ValueType, app?: AppType) => void,
    updateViewCallback: (app?: AppType) => void,
    closeCallback: VoidFunction | undefined,
    toString: (value: ValueType | undefined, app?: AppType) => string,
    toIcon?: (value: ValueType | undefined) => React.ReactNode,
): (value: ValueType) => SettingSubPositionOption<ValueType> {
    return (value: ValueType, app?: AppType): SettingSubPositionOption<ValueType> => {
        return {
            label: toString(value, app),
            icon: toIcon,
            value: value,
            'data-testid': getAllowedSettingsValueTypeString<ValueType>(value, toString(value, app)),
            set: async (): Promise<void> => {
                // apply change
                changeCallback(value);
                // show change
                updateViewCallback(app);
                // close
                if (closeCallback != null) {
                    window.setTimeout(closeCallback, 200);
                }
            }
        };
    };
}

export function getSortedLanguages(languages: Array<string> | ReadonlyArray<string>): Array<string> {
    return languages
        .map((value: string) => ([value, getTranslatedLanguage(value)]))
        .sort((valueA, valueB) => valueA[1].localeCompare(valueB[1]))
        .map((value) => value[0]);
}
